import { useEffect, useRef, useState } from "react";
import { X, Mic, MicOff, Loader2, Check, Volume2, Languages } from "lucide-react";
import api from "../lib/api";
import { i18nLocale } from "../lib/locale";

const LANGUAGES = [
  { code: "fr", label: "Français", bcp47: i18nLocale(), flag: "🇫🇷", intro: "Bonjour ! Je suis votre assistant vocal Apocia. Je vais vous poser quelques questions. Maintenez le bouton micro pour parler." },
  { code: "en", label: "English", bcp47: "en-GB", flag: "🇬🇧", intro: "Hello! I am your Apocia voice assistant. I will ask you a few questions. Hold the mic button to speak." },
  { code: "es", label: "Español", bcp47: "es-ES", flag: "🇪🇸", intro: "Hola, soy tu asistente de voz de Apocia. Te haré algunas preguntas. Mantén pulsado el micrófono para hablar." },
  { code: "de", label: "Deutsch", bcp47: "de-DE", flag: "🇩🇪", intro: "Hallo! Ich bin Ihr Apocia-Sprachassistent. Halten Sie die Mikrofontaste, um zu sprechen." },
  { code: "it", label: "Italiano", bcp47: "it-IT", flag: "🇮🇹", intro: "Ciao! Sono il tuo assistente vocale Apocia. Ti farò alcune domande. Tieni premuto il microfono per parlare." },
  { code: "pt", label: "Português", bcp47: "pt-PT", flag: "🇵🇹", intro: "Olá! Eu sou o seu assistente de voz Apocia. Vou fazer-lhe algumas perguntas. Mantenha o microfone premido para falar." },
  { code: "el", label: "Ελληνικά", bcp47: "el-GR", flag: "🇬🇷", intro: "Γεια σας! Είμαι ο φωνητικός βοηθός Apocia. Κρατήστε πατημένο το μικρόφωνο για να μιλήσετε." },
];

export default function VoiceAgent({ open, onClose, context, onCompleted }) {
  const [lang, setLang] = useState(LANGUAGES[0]);
  const [transcript, setTranscript] = useState([]); // [{role:'agent'|'user', text}]
  const [extracted, setExtracted] = useState({});
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [partial, setPartial] = useState("");
  const [supported, setSupported] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const recognitionRef = useRef(null);
  const transcriptRef = useRef([]);

  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);

  useEffect(() => {
    if (!open) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    setTranscript([]); setExtracted({}); setDone(false); setPartial("");
    // Greeting
    const intro = lang.intro;
    setTranscript([{ role: "agent", text: intro }]);
    speak(intro, lang.bcp47, () => kickoff());
    return () => stopListening();
    // eslint-disable-next-line
  }, [open, lang.code]);

  const speak = (text, bcp47, onEnd) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = bcp47;
      u.rate = 1.0;
      u.pitch = 1.05;
      u.onend = () => onEnd?.();
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {
      onEnd?.();
    }
  };

  const kickoff = async () => {
    // Ask Claude for the first real question
    setBusy(true);
    try {
      const { data } = await api.post("/voice/extract", {
        context,
        language: lang.code,
        transcript: transcriptRef.current,
      });
      handleAgent(data);
    } catch {
      // fallback question
      const q = { fr: "Pour quelle date avez-vous besoin ?", en: "What date do you need?" }[lang.code] || lang.intro;
      pushAgent(q);
    } finally {
      setBusy(false);
    }
  };

  const pushAgent = (text) => {
    setTranscript((t) => [...t, { role: "agent", text }]);
    speak(text, lang.bcp47);
  };

  const handleAgent = (data) => {
    if (data.extracted) setExtracted((prev) => ({ ...prev, ...data.extracted }));
    if (data.next_question) pushAgent(data.next_question);
    if (data.done) {
      setDone(true);
      const final = data.next_question || ({
        fr: "Parfait, j'ai tout. Cliquez sur Enregistrer pour finaliser.",
        en: "Perfect, I have everything. Click Save to finalize.",
      }[lang.code] || "Done");
      // also push final message
      if (!data.next_question) pushAgent(final);
    }
  };

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    const rec = new SR();
    rec.lang = lang.bcp47;
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    let finalText = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      setPartial(interim);
    };
    rec.onerror = () => setListening(false);
    rec.onend = async () => {
      setListening(false);
      setPartial("");
      const said = finalText.trim();
      if (!said) return;
      setTranscript((t) => [...t, { role: "user", text: said }]);
      setBusy(true);
      try {
        const next = [...transcriptRef.current, { role: "user", text: said }];
        const { data } = await api.post("/voice/extract", {
          context,
          language: lang.code,
          transcript: next,
        });
        handleAgent(data);
      } catch (e) {
        pushAgent({ fr: "Désolé, pouvez-vous répéter ?", en: "Sorry, can you repeat?" }[lang.code] || "Sorry?");
      } finally {
        setBusy(false);
      }
    };
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch {}
    setListening(false);
  };

  const submitAll = async () => {
    setSubmitting(true);
    try {
      if (context === "need") {
        await api.post("/needs", {
          start_date: extracted.start_date,
          end_date: extracted.end_date || extracted.start_date,
          start_time: extracted.start_time || "09:00",
          end_time: extracted.end_time || "19:00",
          role_type: extracted.role_type || "Pharmacien",
          skills: extracted.skills || [],
          address: extracted.address || "",
          urgent: !!extracted.urgent,
          hourly_rate: parseFloat(extracted.hourly_rate) || 0,
          notes: extracted.notes || "",
        });
      } else {
        await api.patch("/auth/profile", {
          name: extracted.name,
          professional_role: extracted.professional_role,
          experience_years: parseInt(extracted.experience_years) || 0,
          address: extracted.address,
          phone: extracted.phone || "",
          skills: extracted.skills || [],
          hourly_rate: parseFloat(extracted.hourly_rate) || 0,
          bio: extracted.bio || "",
        });
      }
      onCompleted?.();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-2 sm:p-4" data-testid="voice-agent">
      <div className="bg-white rounded-3xl w-full max-w-2xl h-[88vh] flex flex-col overflow-hidden border border-[#E2E5E0] shadow-2xl">
        <header className="flex items-center justify-between p-4 border-b border-[#E2E5E0]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1A4D2E] grid place-items-center text-white">
              <Volume2 className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-medium text-base" style={{ fontFamily: "Outfit" }}>
                {context === "need" ? "Décrire un besoin à l'oral" : "Créer mon profil à l'oral"}
              </h3>
              <p className="text-xs text-[#6B726A]">Assistant vocal Apocia</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <select
                value={lang.code}
                onChange={(e) => setLang(LANGUAGES.find((l) => l.code === e.target.value))}
                data-testid="voice-lang-select"
                className="appearance-none pl-8 pr-3 py-1.5 text-sm bg-[#F7F8F6] border border-[#E2E5E0] rounded-full focus:border-[#1A4D2E] outline-none"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
                ))}
              </select>
              <Languages className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B726A] pointer-events-none" />
            </div>
            <button onClick={onClose} data-testid="close-voice-agent" className="w-9 h-9 rounded-full hover:bg-[#F0F2EF] grid place-items-center">
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {!supported ? (
          <div className="flex-1 grid place-items-center p-8 text-center">
            <div>
              <MicOff className="w-12 h-12 text-[#6B726A] mx-auto mb-3" />
              <h4 className="font-medium mb-2">Navigateur non compatible</h4>
              <p className="text-sm text-[#6B726A]">
                L'agent vocal nécessite Chrome ou Edge. Utilisez l'entretien chat à la place.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#F7F8F6]">
              {transcript.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#1A4D2E] text-white rounded-br-sm"
                      : "bg-white border border-[#E2E5E0] text-[#1f2421] rounded-bl-sm"
                  }`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {partial && (
                <div className="flex justify-end opacity-60">
                  <div className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm italic bg-[#1A4D2E]/40 text-white">
                    {partial}…
                  </div>
                </div>
              )}
              {busy && (
                <div className="flex justify-start">
                  <div className="px-4 py-2.5 rounded-2xl bg-white border border-[#E2E5E0] text-[#6B726A] flex items-center gap-2 text-sm">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> L'agent réfléchit…
                  </div>
                </div>
              )}
            </div>

            <footer className="border-t border-[#E2E5E0] p-5 bg-white">
              {done ? (
                <button
                  onClick={submitAll}
                  disabled={submitting}
                  data-testid="voice-submit"
                  className="rp-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Enregistrer
                </button>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <button
                    onPointerDown={startListening}
                    onPointerUp={stopListening}
                    onPointerLeave={stopListening}
                    data-testid="voice-mic-button"
                    disabled={busy}
                    className={`w-20 h-20 rounded-full grid place-items-center transition-all select-none ${
                      listening
                        ? "bg-[#E3655B] text-white scale-110 urgent-pulse"
                        : "bg-[#1A4D2E] text-white hover:bg-[#143C24]"
                    } disabled:opacity-50`}
                  >
                    {listening ? <Mic className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
                  </button>
                  <p className="text-xs text-[#6B726A] mt-1">
                    {listening ? "Je vous écoute…" : "Maintenez pour parler"}
                  </p>
                </div>
              )}
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
